import { isFacingTrigger, SYSTEM_FACING_MAX_PATHS, SYSTEM_WORKFLOW_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon } from "@sagot/ui";
import type { ServiceModuleId } from "../../../service/service-modules";
import type { WorkflowDetail } from "../types";
import { canRestore } from "./defaults-model";
import { ModuleOffNote } from "./module-off-note";

const B = "engage.workflows.defaults.builder";

interface DefaultBannerProps {
  workflow: Pick<WorkflowDetail, "is_modified" | "has_default_update" | "has_unpublished_changes" | "state" | "pause_impact" | "trigger">;
  /** The Service module the default needs when it is switched off: the module switch paused it. */
  moduleOff?: ServiceModuleId | null;
  canPublish: boolean;
  /** The plan has workflows of the workspace's own: a default can be duplicated. */
  canDuplicate: boolean;
  onRestore: () => void;
  onCompare: () => void;
}

/**
 * Above the builder of a default: what it is, that it runs on every plan, what the allowance is, and whether somebody changed it
 * or the catalogue moved on (the badges for those sit in the header). A paused default says what that stops. The two actions are also in the More menu.
 */
export function DefaultBanner({ workflow, canPublish, canDuplicate, moduleOff = null, onRestore, onCompare }: DefaultBannerProps) {
  const { t } = useTranslation();
  const paused = workflow.state === "paused";
  return (
    <section className="rounded-lg border border-line bg-surface p-4" aria-labelledby="default-banner-title" data-testid="default-banner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Icon name="info" className="mt-0.5 size-5 shrink-0 text-primary-ink" />
          <div className="min-w-0 space-y-1.5">
            <h2 id="default-banner-title" className="text-sm font-bold">{t(`${B}.bannerTitle`)}</h2>
            <p className="text-sm text-fg-muted">{t(`${B}.banner`)}</p>
            {canDuplicate ? null : <p className="text-xs text-fg-subtle">{t(`${B}.bannerStarter`)}</p>}
            <p className="text-xs text-fg-subtle">{t(`${B}.limits`, { paths: isFacingTrigger(workflow.trigger) ? SYSTEM_FACING_MAX_PATHS : SYSTEM_WORKFLOW_LIMITS.paths, steps: SYSTEM_WORKFLOW_LIMITS.stepsPerPath })}</p>
            {workflow.is_modified ? <p className="text-xs font-semibold text-accent-ink" role="status">{t(`${B}.bannerModified`)}</p> : null}
            {workflow.has_default_update ? <p className="text-xs font-semibold text-accent-ink" role="status">{t(`${B}.bannerUpdate`)}</p> : null}
            {paused && workflow.pause_impact ? <p className="text-xs font-semibold" role="status" data-testid="paused-note">{t(`${B}.pausedNote`, { impact: workflow.pause_impact })}</p> : null}
            {paused && moduleOff ? <ModuleOffNote module={moduleOff} /> : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={onCompare}>
            <Icon name="layers" />
            {t(`${B}.compare`)}
          </Button>
          {canPublish ? (
            <Button variant="secondary" size="sm" disabled={!canRestore(workflow)} onClick={onRestore}>
              <Icon name="rotate-ccw" />
              {t(`${B}.restore`)}
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
